import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Check, Clock3, ShieldAlert } from 'lucide-react';
import { idemApi } from '../lib/api';
import { setIdemDeviceToken } from '../lib/idemSession';

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function statusLabel(status) {
  return String(status || 'pending').replaceAll('_', ' ');
}

export default function IdemActivate() {
  const { token } = useParams();
  const encodedToken = encodeURIComponent(token || '');
  const navigate = useNavigate();
  const [activation, setActivation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await idemApi.get(`/idem/activate/${encodedToken}`);
      setActivation(data.activation || null);
    } catch (requestError) {
      setActivation(null);
      setError(requestError?.response?.data?.error ? 'This activation link is invalid or expired.' : 'The activation link could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [encodedToken]);

  useEffect(() => {
    load();
  }, [load]);

  const confirm = async () => {
    setWorking(true);
    setNotice('');
    setError('');
    try {
      const { data } = await idemApi.post(`/idem/activate/${encodedToken}`);
      if (data.device_token) {
        setIdemDeviceToken(data.device_token);
      }
      setNotice('Lab authenticator paired successfully. Redirecting to your account...');
      navigate('/idem/account', { replace: true });
    } catch (requestError) {
      setError(requestError?.response?.data?.error || 'The activation could not be completed.');
      await load();
    } finally {
      setWorking(false);
    }
  };

  const status = activation?.status || 'pending';
  const canActivate = status === 'pending';

  return (
    <main className="min-h-screen bg-zinc-100 px-4 py-6 text-zinc-900 sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-3xl">
        <header className="flex items-center gap-2.5 border-b border-zinc-200 pb-5">
          <div className="grid h-9 w-9 place-items-center bg-black text-sm font-semibold text-white">i</div>
          <div>
            <p className="font-display text-lg font-bold tracking-tight">idem</p>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Lab activation</span>
          </div>
        </header>

        <section className="mt-5 border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="font-medium">Lab authenticator device</p>
              <p className="mt-1 text-xs leading-5">This is an internal idem lab pairing flow only. It does not create a production CSAM/FAS authenticator, public login, OIDC provider, official LoA, or regulated identifier flow.</p>
            </div>
          </div>
        </section>

        {notice && <div className="mt-5 border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{notice}</div>}
        {error && <div className="mt-5 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        {loading ? (
          <div className="mt-6 border border-zinc-200 bg-white p-5 text-sm text-zinc-500">Loading activation link…</div>
        ) : activation ? (
          <section className="mt-6 border border-zinc-200 bg-white p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Activation status</p>
                <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">{statusLabel(status)}</h1>
                <p className="mt-2 max-w-2xl text-sm text-zinc-600">Confirm the lab authenticator pairing for the linked digital key account.</p>
              </div>
              <span className="inline-flex border border-zinc-200 bg-zinc-50 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-zinc-600">{statusLabel(status)}</span>
            </div>

            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <article className="border border-zinc-100 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Digital key account</p>
                <p className="mt-1 break-all font-mono text-xs text-zinc-700">{activation.digital_key_account_id}</p>
                <p className="mt-1 text-xs text-zinc-500">{activation.account_status || '—'}</p>
              </article>
              <article className="border border-zinc-100 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Identity reference</p>
                <p className="mt-1 text-sm text-zinc-700">{activation.identity_display_name || activation.identity_public_id || '—'}</p>
                <p className="mt-1 font-mono text-xs text-zinc-500">{activation.identity_public_id || activation.identity_id || '—'}</p>
              </article>
              <article className="border border-zinc-100 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Activation expires</p>
                <p className="mt-1 text-sm text-zinc-700">{formatDate(activation.activation_token_expires_at)}</p>
              </article>
              <article className="border border-zinc-100 p-3">
                <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Source verification</p>
                <p className="mt-1 text-sm text-zinc-700">{String(activation.source_verification_method || '—').replaceAll('_', ' ')}</p>
                <p className="mt-1 text-xs text-zinc-500">{String(activation.source_verification_status || '—').replaceAll('_', ' ')}</p>
              </article>
            </div>

            <div className="mt-5 border-t border-zinc-100 pt-4">
              <div className="flex items-center gap-2">
                <Clock3 className="h-4 w-4 text-zinc-500" strokeWidth={1.5} />
                <p className="text-sm font-medium text-zinc-800">Confirm pairing</p>
              </div>
              <p className="mt-2 text-sm text-zinc-600">This creates a paired lab authenticator device for the internal idem demo flow. The raw device token is shown only once after confirmation.</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={confirm}
                  disabled={!canActivate || working}
                  className="inline-flex items-center gap-1.5 border border-emerald-300 px-3 py-2 text-sm text-emerald-700 hover:border-emerald-500 disabled:opacity-50"
                >
                  <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Confirm activation
                </button>
                <Link to="/idem/account" className="border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">
                  View account
                </Link>
              </div>
            </div>
          </section>
        ) : (
          <section className="mt-6 border border-zinc-200 bg-white p-5 text-sm text-zinc-600">
            <p className="font-medium text-zinc-800">Activation unavailable</p>
            <p className="mt-1">This activation link could not be loaded.</p>
          </section>
        )}
      </div>
    </main>
  );
}
